import { useQueryClient } from 'react-query';

const LIST_OVERRIDE_BASE_KEY = 'override';
const AUDIO_PICKER_BASE_KEY = 'audioPicker';
const PHONE_TREE_BASE_KEY = 'phone-tree';

export const queryKeys = {
  base: ['phone'] as const,
  settings: {
    base: 'phone-settings' as const,
    audioLibrary: () => [...queryKeys.base, 'audioLibrary'],
    audioLibraryUsage: (id: string) => [...queryKeys.base, 'audioLibraryUsage', id],
    audioLibraryUsageV2: (id: string) => [...queryKeys.base, 'audioLibraryUsageV2', id],
    listCallBlock: () => [queryKeys.settings.base, 'call-block'],
    listCallQueue: () => [queryKeys.settings.base, 'call-queue'],
    readCallQueue: (id: string) => [queryKeys.settings.base, 'call-queue', id],
    listAvailableDevices: () => [queryKeys.settings.base, 'available-devices'],
    listDeviceSipProfiles: (id: string) => [queryKeys.settings.base, id, 'device'],
    listCallGroupsbySipProfile: (id: string) => [queryKeys.settings.base, id, 'call-groups'],
    listForwardingNumbers: (id: string) => [queryKeys.settings.base, id, 'forwarding-numbers'],
    listPhoneHours: () => [queryKeys.settings.base, 'phone-hours'],
    readPhoneHour: (id: string) => [queryKeys.settings.base, 'phone-hours', id],
    listPhoneTrees: () => [queryKeys.settings.base, PHONE_TREE_BASE_KEY],
    readPhoneTree: (id: string) => [queryKeys.settings.base, PHONE_TREE_BASE_KEY, id],
    listOverrides: (options?: { includeDeviceExtensionOverrides?: boolean }) => [
      queryKeys.settings.base,
      LIST_OVERRIDE_BASE_KEY,
      options,
    ],
    listVoicemailBoxUnavailableExtensions: () => [queryKeys.settings.base, 'voicemail-box-unavailable-extensions'],
    listVoicemailBox: () => [queryKeys.settings.base, 'voicemail-box-list'],
    readVoicemailBox: (id: string) => [queryKeys.settings.base, 'voicemail-box', id],
    listVoicemailBoxGreetings: (id: string) => [queryKeys.settings.base, 'voicemail-box-greetings', id],
    listVoicemailGreetings: () => [queryKeys.settings.base, 'voicemail-greetings'],
    listVoicemailBoxUsage: (id: string) => [queryKeys.settings.base, 'voicemail-box-usage', id],
    listVoicemailExtensions: () => [queryKeys.settings.base, 'voicemail-extensions'],
    countVoicemails: (id: string) => [queryKeys.settings.base, 'voicemail-count', id],
    listSystemMedia: () => [queryKeys.settings.base, 'system-media'],
    listLocationHoldMusic: () => [queryKeys.settings.base, 'location-hold-music'],
    listLineKeys: () => [queryKeys.settings.base, 'device', 'line-keys'],
    listDeviceModels: () => [queryKeys.settings.base, 'line-keys', 'device-models'],
  },
  audioPicker: (options?: Record<string, any>) => [...queryKeys.base, AUDIO_PICKER_BASE_KEY, options],
  callBlocking: (id: string) => [...queryKeys.base, 'callBlocking', id],
  callBlockingList: () => [...queryKeys.base, 'callBlockingList'],
  callGroup: (id: string) => [...queryKeys.base, 'callGroup', id],
  callGroups: () => [...queryKeys.base, 'callGroups'],
  callGroupUsage: (id: string) => [...queryKeys.base, 'callGroupsUsage', id],
  callQueue: (id: string) => [...queryKeys.base, 'callQueue', id],
  callQueues: () => [...queryKeys.base, 'callQueues'],
  callQueueUsage: (id: string) => [...queryKeys.base, 'callQueueUsage', id],
  callRecords: (query?: string) => [...queryKeys.base, 'callRecords', query],
  department: (id: string) => [...queryKeys.base, 'department', id],
  departmentForwardingOverride: (id: string) => [...queryKeys.base, 'departmentForwardingOverride', id],
  departmentVMOverride: (id: string) => [...queryKeys.base, 'departmentVMOverride', id],
  departmentVoicemailBox: (id: string) => [...queryKeys.base, 'departmentVoicemailBox', id],
  deviceById: (id: string) => [...queryKeys.base, 'deviceById', id],
  devices: () => [...queryKeys.base, 'devices'],
  devicesWithAddresses: () => [...queryKeys.base, 'devicesWithAddresses'],
  familyLocations: (id: string) => [...queryKeys.base, 'familyLocations', id],
  forwardingNumbers: () => [...queryKeys.base, 'forwardingNumbers'],
  greetingSchedules: (id: string) => [...queryKeys.base, 'greetingSchedules', id],
  holdMusic: () => [...queryKeys.base, 'holdMusic'],
  mainlineDepartment: () => [...queryKeys.base, 'mainlineDepartment'],
  listCallGroups: (tenantId: string) => [...queryKeys.base, 'listCallGroups', tenantId],
  listCallRecording: (tenantId: string) => [...queryKeys.base, 'listCallRecording', tenantId],
  listDepartments: () => [...queryKeys.base, 'listDepartments'],
  listDepartmentsWithSchedules: () => [...queryKeys.base, 'listDepartmentsWithSchedules'],
  listDepartmentForwardNumbers: (id: string) => [...queryKeys.base, 'listDepartmentForwardNumbers', id],
  listDepartmentPhoneNumbers: (id: string) => [...queryKeys.base, 'listDepartmentPhoneNumbers', id],
  listDepartmentSchedules: (id: string) => [...queryKeys.base, 'listDepartmentSchedules', id],
  listLegacyLocationSchedules: (id: string) => [...queryKeys.base, 'listLegacyLocationSchedules', id],
  overrideList: () => [...queryKeys.base, 'listOverride'],
  phoneMedia: () => [...queryKeys.base, 'phoneMedia'],
  phoneTree: (id: string) => [...queryKeys.base, 'phoneTree', id],
  phoneTrees: (tenantId?: string) => [...queryKeys.base, 'phoneTrees', tenantId],
  ringtones: (id: string) => [...queryKeys.base, 'ringtones', id],
  selectedHoldMusic: () => [...queryKeys.base, 'selectedHoldMusic'],
  timezone: (locationId: string) => [...queryKeys.base, 'timezone', locationId],
  voiceMailbox: (id: string) => [...queryKeys.base, 'voiceMailbox', id],
  voiceMailboxes: () => [...queryKeys.base, 'voiceMailboxes'],
  voiceMailGreetings: (id: string) => [...queryKeys.base, 'voiceMailGreetings', id],
  voiceMailGreetingMap: (id: string) => [...queryKeys.base, 'voiceMailGreetingMap', id],
  voicemailOverride: () => [...queryKeys.base, 'voicemailOverride'],
  voicemailMessage: (mailboxId: string, messageId: string) => [
    ...queryKeys.base,
    'voicemailMessage',
    mailboxId,
    messageId,
  ],
  voicemailTranscription: (id: string) => [...queryKeys.base, 'voicemailTranscription', id],
  voicemailExtensions: (tenantId: string) => [...queryKeys.base, 'voicemailExtensions', tenantId],
  listTenantVoicemailBoxes: (tenantId: string) => [...queryKeys.base, 'listTenantVoicemailBoxes', tenantId],
  tenantVoicemailBox: (tenantId: string) => [...queryKeys.base, 'tenantVoicemailBox', tenantId],
};

// Invalidates override list queries for a given phone tenant ID. This is so that it will invalidate no matter the options
// used in the query key since the options object can vary (e.g. includeDeviceExtensionOverrides: true/false).
export const invalidateListOverrideQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  phoneTenantId: string
) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey.some((key) => typeof key === 'string' && key.includes(LIST_OVERRIDE_BASE_KEY)) &&
      query.queryKey.includes(phoneTenantId),
  });
};

// Invalidates audio picker queries for a given tenant ID. This is so that it will invalidate no matter the options
// used in the query key since the options object can vary.
export const invalidateAudioPickerQueries = (queryClient: ReturnType<typeof useQueryClient>, phoneTenantId: string) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey.some((key) => typeof key === 'string' && key.includes(AUDIO_PICKER_BASE_KEY)) &&
      query.queryKey.includes(phoneTenantId),
  });
};

// Invalidates phone tree related queries for a given tenant ID. This is so that it will invalidate both list and read queries.
export const invalidatePhoneTreeQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey.some((key) => typeof key === 'string' && key.includes(PHONE_TREE_BASE_KEY)),
  });
};
